import { tss } from 'tss-react/mui';

export const SELECT_VOICES_LIST_MAX_HEIGHT = 360;
export const SELECT_VOICES_ROW_HEIGHT = 48;
/** Max height of the closed control (chips + search input). */
export const SELECT_VOICES_CONTROL_MAX_HEIGHT = 176;
/** Scrollable chips area inside the control (~4–5 rows). */
export const SELECT_VOICES_CHIPS_MAX_HEIGHT = 132;

export const useStyles = tss.create(({ theme }) => ({
  root: {
    position: 'relative',
    width: '100%',
    minWidth: 0,
  },
  rootOpen: {
    zIndex: theme.zIndex.modal,
  },
  control: {
    width: '100%',
    minWidth: 0,
    minHeight: 42,
    maxHeight: SELECT_VOICES_CONTROL_MAX_HEIGHT,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.background.default,
    padding: '6px 36px 6px 10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 4,
    cursor: 'text',
    position: 'relative',
    fontFamily: 'inherit',
    overflow: 'hidden',
    '&:hover:not(.disabled)': {
      borderColor: theme.palette.primary.main,
    },
  },
  chipsScroll: {
    flex: '1 1 auto',
    minHeight: 0,
    maxHeight: SELECT_VOICES_CHIPS_MAX_HEIGHT,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    gap: 6,
    paddingRight: 2,
  },
  inputRow: {
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    minHeight: 28,
  },
  controlFocused: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  controlDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  inlineInput: {
    flex: '1 1 80px',
    minWidth: 80,
    minHeight: 28,
    alignSelf: 'stretch',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: theme.palette.text.primary,
    fontSize: 14,
    lineHeight: 1.4,
    fontFamily: 'inherit',
    padding: '2px 4px',
    '&::placeholder': {
      color: theme.palette.extendedGrey?.neutral ?? theme.palette.text.secondary,
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
  },
  chevron: {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.text.secondary,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: '100%',
    padding: '2px 4px 2px 10px',
    borderRadius: 6,
    background: theme.palette.primary.light,
    color: theme.palette.primary.main,
    fontSize: 13,
    lineHeight: 1.3,
    fontWeight: 500,
    flexShrink: 0,
  },
  chipLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 200,
  },
  chipRemove: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.06)',
    },
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: -1,
    zIndex: 1,
    borderRadius: '0 0 8px 8px',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderTop: 'none',
    boxShadow: '0px 8px 16px rgba(42, 53, 71, 0.12)',
    background: theme.palette.background.default,
  },
  dropdownPortalPaper: {
    marginTop: -1,
    borderRadius: '0 0 8px 8px',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderTop: 'none',
    boxShadow: '0px 8px 16px rgba(42, 53, 71, 0.12)',
    background: theme.palette.background.default,
  },
  list: {
    maxHeight: SELECT_VOICES_LIST_MAX_HEIGHT,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '4px 0',
    background: theme.palette.background.default,
  },
  row: {
    width: '100%',
    height: SELECT_VOICES_ROW_HEIGHT,
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '0 12px',
    cursor: 'pointer',
    textAlign: 'start',
    fontFamily: 'inherit',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  rowActive: {
    background: theme.palette.action.hover,
  },
  rowSelected: {
    background: theme.palette.primary.light,
    '&:hover': {
      background: theme.palette.primary.light,
    },
  },
  rowTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
    fontSize: 14,
  },
  providerBadge: {
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    padding: '2px 6px',
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    background: theme.palette.background.default,
    maxWidth: 90,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  empty: {
    padding: '18px 12px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  checkIcon: {
    fontSize: 16,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
}));
